import { FileOutlined, FilePdfOutlined } from "@ant-design/icons"
import { Alert, App, Button, Empty, Popconfirm } from "antd"
import {
  type InternalUploadFile,
  type UploadChangeParam,
} from "antd/es/upload/interface"
import dayjs from "dayjs"
import { compact, entries, isEmpty, keyBy, map, uniq } from "lodash"
import { useMemo, useState } from "react"

import { classAtoms, tryoutAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import Uploader from "#blocks/Uploader"
import Firebase from "#classes/Firebase"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  type FirebaseBatchUpdate,
  type FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  UploadFile,
  UploadStatus,
} from "#customTypes/composedTypes/firestoreTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { COLORS } from "#styles/colors"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { STANDARD_DATE_FORMAT } from "#utils/dateHelpers"

interface DocumentItemProps {
  file: UploadFile
  label: string
  sublabel?: string
}

const DocumentItem = ({ file, label, sublabel }: DocumentItemProps) => {
  const isPdf =
    file.name?.toLowerCase().endsWith(".pdf") ||
    file.type?.includes("pdf") ||
    file.url?.includes(".pdf")

  const handleOpen = () => {
    if (file.url) {
      window.open(file.url, "_blank")
    }
  }

  return (
    <Flex
      style={{
        padding: "12px 16px",
        border: `1px solid ${COLORS.GRAY4}`,
        borderRadius: 8,
        backgroundColor: COLORS.GRAY1,
        alignItems: "center",
        gap: 12,
      }}
    >
      {isPdf ? (
        <FilePdfOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
      ) : (
        <FileOutlined style={{ fontSize: 24, color: COLORS.GRAY7 }} />
      )}
      <Flex
        col
        style={{
          flex: 1,
          minWidth: 0,
          fontWeight: 500,
          fontSize: 14,
          color: COLORS.GRAY8,
        }}
      >
        <TextOverrun text={label} />
        {sublabel && (
          <span style={{ fontSize: 12, color: COLORS.GRAY7, fontWeight: 400 }}>
            {sublabel}
          </span>
        )}
      </Flex>
      <Button
        type="link"
        size="small"
        onClick={handleOpen}
        disabled={!file.url}
      >
        View
      </Button>
    </Flex>
  )
}

interface StudentDocumentsSectionProps {
  files: StudentConstructor["files"]
  studentId: Uuid | undefined
}

const RELEASE_WAIVER_LABEL = "Signed Release Form"
const RELEASE_WAIVER_FILENAME = "waiver"
const MISSING_STUDENT_ID = "missing-student-id"

const StudentDocumentsSection = ({
  files,
  studentId,
}: StudentDocumentsSectionProps) => {
  const { message } = App.useApp()
  const canUploadDocuments = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.UPLOAD_DOCUMENT
  )
  const [uploadingWaiverFile, setUploadingWaiverFile] =
    useState<InternalUploadFile | null>(null)
  const [isReplacingWaiver, setIsReplacingWaiver] = useState(false)
  const [isSyncingWaiver, setIsSyncingWaiver] = useState(false)
  const tryoutIds = useMemo(
    () => (files?.tryoutEvals ? Object.keys(files.tryoutEvals) : []),
    [files?.tryoutEvals]
  )
  const studentTryoutsQueryConfig = useMemo<FirebaseQueryConfig>(
    () => ({
      queries: {
        studentId: {
          value: studentId ?? MISSING_STUDENT_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )

  const tryouts = useSomeDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    tryoutIds
  )
  const studentTryouts = useFilteredDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    studentTryoutsQueryConfig
  )

  // Extract classIds from tryout sessionIds to fetch class names
  const classIds = useMemo(() => {
    return uniq(
      compact(
        tryouts.map((t) =>
          t.sessionId ? getSessionInfoFromId(t.sessionId)[0] : null
        )
      )
    )
  }, [tryouts])

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classIds
  )

  const classesById = useMemo(() => keyBy(classes, "id"), [classes])

  const waiver = files?.waiver
  const tryoutEvals = files?.tryoutEvals

  const waiverFileList: InternalUploadFile[] = uploadingWaiverFile
    ? [uploadingWaiverFile]
    : waiver && !isReplacingWaiver
      ? [
          {
            uid: waiver.uid ?? RELEASE_WAIVER_FILENAME,
            name: waiver.name ?? RELEASE_WAIVER_LABEL,
            status: waiver.status ?? UploadStatus.DONE,
            url: waiver.url,
            percent: waiver.percent ?? 100,
          } as InternalUploadFile,
        ]
      : []

  const releaseFormMetadata = {
    studentId,
    contentDisposition: `inline; filename="${RELEASE_WAIVER_LABEL}"`,
  }

  const syncWaiverToStudentAndTryouts = async (
    waiverFile: UploadFile | null
  ) => {
    if (!studentId) return

    const hasSignedReleaseForm = waiverFile != null
    const uploadedReleaseForm = waiverFile?.url ?? null
    const tryoutUpdates = studentTryouts.reduce<
      Record<Uuid, Record<string, unknown>>
    >((acc, tryout) => {
      acc[tryout.id] = {
        "step1.hasSignedReleaseForm": hasSignedReleaseForm,
        "step1.uploadedReleaseForm": uploadedReleaseForm,
      }

      return acc
    }, {})

    const batchUpdate: Record<
      string,
      Record<string, Record<string, unknown>>
    > = {
      [DOC_TYPES.STUDENTS]: {
        [studentId]: {
          "files.waiver": waiverFile,
        },
      },
    }

    if (studentTryouts.length > 0) {
      batchUpdate[DOC_TYPES.TRYOUTS] = tryoutUpdates
    }

    setIsSyncingWaiver(true)
    try {
      await Firebase.getInstance().updateDocuments(
        batchUpdate as unknown as FirebaseBatchUpdate
      )
    } finally {
      setIsSyncingWaiver(false)
    }
  }

  const handleWaiverUploadStatusChange = async (info: UploadChangeParam) => {
    if (!studentId) return

    const { status } = info.file

    if (status === UploadStatus.UPLOADING) {
      setUploadingWaiverFile({
        uid: info.file.uid,
        name: RELEASE_WAIVER_LABEL,
        status: UploadStatus.UPLOADING,
        percent: info.file.percent ?? 0,
      } as InternalUploadFile)
      return
    }

    if (status === UploadStatus.DONE) {
      setUploadingWaiverFile(null)

      const result: UploadFile = {
        url: info.file.response,
        name: RELEASE_WAIVER_LABEL,
        size: info.file.size,
        status: UploadStatus.DONE,
        uid: info.file.uid,
        percent: info.file.percent ?? 100,
      }

      await syncWaiverToStudentAndTryouts(result)
      setIsReplacingWaiver(false)

      message.success(`File ${info.file.name} uploaded successfully.`)
      return
    }

    if (status === UploadStatus.ERROR) {
      setUploadingWaiverFile(null)
      message.error(`File ${info.file.name} upload failed.`)
    }
  }

  const handleDeleteWaiverFile = async () => {
    if (!studentId) return

    setUploadingWaiverFile(null)
    await syncWaiverToStudentAndTryouts(null)
    setIsReplacingWaiver(false)
    message.success("Release waiver removed.")
  }

  const getTryoutLabel = (tryoutId: Uuid) => {
    const tryout = tryouts.find((t) => t.id === tryoutId)
    const classId = tryout?.sessionId
      ? getSessionInfoFromId(tryout.sessionId)[0]
      : null
    const className = classId ? classesById[classId]?.name : null

    const parts: string[] = ["Tryout Eval"]
    if (className) parts.push(className)
    if (tryout?.createdAt) {
      parts.push(dayjs(tryout.createdAt).format(STANDARD_DATE_FORMAT))
    }

    return parts.join(" - ")
  }

  return (
    <Flex
      col
      gap={24}
      style={{ overflowY: "auto", paddingRight: 20, maxWidth: 500 }}
    >
      {/* Waiver Section */}
      <Flex col gap={12}>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: COLORS.GRAY8,
            margin: 0,
          }}
        >
          Release Waiver
        </h3>
        {canUploadDocuments ? (
          <Alert
            type="warning"
            showIcon
            message="Only upload signed waiver forms here."
            description="Uploading, replacing, or removing this file updates the student's stored waiver and the signed-release-form state on every tryout for this student."
          />
        ) : null}
        {waiver && <DocumentItem file={waiver} label={RELEASE_WAIVER_LABEL} />}
        {canUploadDocuments && studentId && (!waiver || isReplacingWaiver) ? (
          <Uploader
            fileList={waiverFileList}
            onStatusChange={handleWaiverUploadStatusChange}
            onDelete={handleDeleteWaiverFile}
            folder={`${studentId}/files`}
            filename={RELEASE_WAIVER_FILENAME}
            metadata={releaseFormMetadata}
          />
        ) : null}
        {canUploadDocuments && waiver && studentId && !isReplacingWaiver ? (
          <Flex gap={8}>
            <Popconfirm
              title="Replace the existing waiver?"
              description="This will replace the student waiver and mark every tryout for this student as using the new signed release form."
              onConfirm={() => setIsReplacingWaiver(true)}
              okText="Yes, replace it"
              cancelText="No"
            >
              <Button>Replace waiver</Button>
            </Popconfirm>
            <Popconfirm
              title="Remove the existing waiver?"
              description="This will remove the student waiver and uncheck signed release form on every tryout for this student."
              onConfirm={handleDeleteWaiverFile}
              okText="Yes, remove it"
              cancelText="No"
              okButtonProps={{ loading: isSyncingWaiver }}
            >
              <Button danger loading={isSyncingWaiver}>
                Remove waiver
              </Button>
            </Popconfirm>
          </Flex>
        ) : null}
        {canUploadDocuments && waiver && studentId && isReplacingWaiver ? (
          <Button onClick={() => setIsReplacingWaiver(false)}>
            Cancel replacement
          </Button>
        ) : null}
        {!waiver && (!studentId || !canUploadDocuments) ? (
          <Empty
            description="No release waiver uploaded"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : null}
      </Flex>

      {/* Tryout Evals Section */}
      <Flex col gap={12}>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: COLORS.GRAY8,
            margin: 0,
          }}
        >
          Tryout Evaluations
        </h3>
        {!isEmpty(tryoutEvals) ? (
          <Flex col gap={8}>
            {map(entries(tryoutEvals), ([tryoutId, file]) => (
              <DocumentItem
                key={tryoutId}
                file={file}
                label={getTryoutLabel(tryoutId)}
              />
            ))}
          </Flex>
        ) : (
          <Empty
            description="No tryout evaluations uploaded"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Flex>
    </Flex>
  )
}

export default StudentDocumentsSection
